// Modification groupée en démonstration : le rapport est calculé localement,
// avec le même calcul de prix que l'API (`appliquerReglePrix`). Rien n'est
// écrit : les œuvres de la démonstration restent celles des fixtures.
import {
  PLAFOND_LOT_OEUVRES,
  appliquerReglePrix,
  type DemandeLotOeuvres,
  type LigneLotOeuvres,
  type RapportLotOeuvres,
} from '@artosera/shared';
import { DEMO_OEUVRES } from './demoFixtures.js';
import { ApiError } from './errors.js';

const REFUS_PAR_STATUT: Readonly<Record<string, { code: string; motif: string }>> = {
  vendue: { code: 'oeuvre_vendue', motif: 'Cette œuvre est vendue : elle ne change plus.' },
  reservee: {
    code: 'oeuvre_reservee',
    motif: 'Cette œuvre est réservée : terminez d’abord la réservation.',
  },
  louee: { code: 'oeuvre_louee', motif: 'Cette œuvre est louée : terminez d’abord la location.' },
  archivee: { code: 'oeuvre_archivee', motif: 'Cette œuvre est archivée.' },
};

export function identifiantsDemo(ids: readonly string[]): {
  ids: readonly string[];
  total: number;
  plafond: number;
  tronque: boolean;
} {
  return {
    ids: ids.slice(0, PLAFOND_LOT_OEUVRES),
    total: ids.length,
    plafond: PLAFOND_LOT_OEUVRES,
    tronque: ids.length > PLAFOND_LOT_OEUVRES,
  };
}

function ligneDemo(demande: DemandeLotOeuvres, id: string): LigneLotOeuvres {
  const oeuvre = DEMO_OEUVRES.find((candidate) => candidate.id === id);
  if (!oeuvre) {
    return {
      oeuvreId: id,
      titre: null,
      numeroInventaire: null,
      resultat: 'refusee',
      code: 'introuvable',
      motif: 'Cette œuvre est introuvable.',
    };
  }
  const base = { oeuvreId: id, titre: oeuvre.titre, numeroInventaire: oeuvre.numeroInventaire };
  const action = demande.action;
  const refus = REFUS_PAR_STATUT[oeuvre.statut];
  const refusee = (motif: { code: string; motif: string }): LigneLotOeuvres => ({
    ...base,
    resultat: 'refusee',
    ...motif,
  });
  const modifiee = (avant: Record<string, unknown>, apres: Record<string, unknown>) =>
    ({ ...base, resultat: 'modifiee', avant, apres }) satisfies LigneLotOeuvres;
  const inchangee = { ...base, resultat: 'inchangee' } satisfies LigneLotOeuvres;

  switch (action.type) {
    case 'prix': {
      if (refus && oeuvre.statut !== 'louee') return refusee(refus);
      if (oeuvre.prixPublicCentimes === null) {
        return refusee({ code: 'sans_prix', motif: 'Cette œuvre n’a pas de prix public.' });
      }
      if (action.regle.mode === 'montant' && action.regle.devise !== oeuvre.devise) {
        return refusee({
          code: 'devise_differente',
          motif: `Cette œuvre est en ${oeuvre.devise} : le montant fixe est dans une autre devise.`,
        });
      }
      const nouveau = appliquerReglePrix(oeuvre.prixPublicCentimes, action.regle, action.arrondi);
      if (nouveau === null) {
        return refusee({ code: 'prix_invalide', motif: 'Le nouveau prix ne serait pas valable.' });
      }
      if (nouveau === oeuvre.prixPublicCentimes) return inchangee;
      return modifiee(
        { prixPublicCentimes: oeuvre.prixPublicCentimes, devise: oeuvre.devise },
        { prixPublicCentimes: nouveau, devise: oeuvre.devise },
      );
    }
    case 'statut':
      if (refus) return refusee(refus);
      if (oeuvre.statut === action.vers) return inchangee;
      return modifiee({ statut: oeuvre.statut }, { statut: action.vers });
    case 'archivage':
      if (oeuvre.statut === 'archivee') return inchangee;
      if (oeuvre.statut === 'reservee') return refusee(REFUS_PAR_STATUT['reservee']!);
      return modifiee({ statut: oeuvre.statut }, { statut: 'archivee' });
    case 'publication':
      if (oeuvre.statut === 'archivee' || oeuvre.statut === 'retiree') {
        return refusee({
          code: oeuvre.statut === 'archivee' ? 'oeuvre_archivee' : 'oeuvre_retiree',
          motif: 'Une œuvre retirée ou archivée ne se publie pas.',
        });
      }
      if (oeuvre.visibleSurLeSite === action.visible) return inchangee;
      return modifiee(
        { visibleSurLeSite: oeuvre.visibleSurLeSite },
        { visibleSurLeSite: action.visible },
      );
    case 'localisation':
      if (oeuvre.statut === 'archivee') return refusee(REFUS_PAR_STATUT['archivee']!);
      if ((oeuvre.localisationId ?? null) === action.lieuId) return inchangee;
      return modifiee(
        { localisationId: oeuvre.localisationId ?? null, emplacementId: null },
        { localisationId: action.lieuId, emplacementId: null },
      );
    case 'exposition':
    case 'selection':
      if (oeuvre.statut === 'archivee') return refusee(REFUS_PAR_STATUT['archivee']!);
      return { ...base, resultat: 'modifiee' };
  }
}

function empreinteDemo(lignes: readonly LigneLotOeuvres[]): string {
  const texte = JSON.stringify(lignes);
  let hash = 5381;
  for (let i = 0; i < texte.length; i += 1) hash = (hash * 33) ^ texte.charCodeAt(i);
  return `demo-${(hash >>> 0).toString(16)}`;
}

export function lotDemo(corps: unknown): RapportLotOeuvres {
  const demande = corps as DemandeLotOeuvres | undefined;
  if (!demande || !Array.isArray(demande.oeuvreIds) || !demande.action) {
    throw new ApiError(400, 'validation.invalide', 'Les données envoyées sont invalides.');
  }
  if (demande.oeuvreIds.length > PLAFOND_LOT_OEUVRES) {
    throw new ApiError(
      400,
      'validation.invalide',
      `Un lot compte au plus ${String(PLAFOND_LOT_OEUVRES)} œuvres : découpez la sélection.`,
    );
  }
  const lignes = demande.oeuvreIds.map((id) => ligneDemo(demande, id));
  const compter = (resultat: LigneLotOeuvres['resultat']): number =>
    lignes.filter((ligne) => ligne.resultat === resultat).length;
  const essai = demande.essai === true;
  return {
    lot: {
      id: essai ? null : 'demo-lot',
      action: demande.action.type,
      essai,
      rejoue: false,
      empreinte: empreinteDemo(lignes),
      creeLe: essai ? null : new Date().toISOString(),
    },
    compteurs: {
      demandees: lignes.length,
      modifiees: compter('modifiee'),
      inchangees: compter('inchangee'),
      refusees: compter('refusee'),
    },
    lignes,
  };
}
